<script setup lang="ts">
import { computed, ref } from 'vue'
import { BLOCKS, QUESTIONS, type Question } from '../../lib/issue_diagnosis/questions'
import type { Answers } from '../../lib/issue_diagnosis/types'

const props = defineProps<{ answers: Answers }>()
const emit = defineEmits<{ complete: []; exit: [] }>()

const visibleQuestions = computed<Question[]>(() =>
  QUESTIONS.filter((q) => !q.skipWhen || !q.skipWhen(props.answers as unknown as Record<string, string | string[]>))
)

const index = ref(0)
const currentQuestion = computed(() => visibleQuestions.value[index.value])
const blockIndex = computed(() => (BLOCKS as readonly string[]).indexOf(currentQuestion.value.block))
const remaining = computed(() => visibleQuestions.value.length - index.value - 1)
const progressPercent = computed(() => ((index.value + 1) / visibleQuestions.value.length) * 100)

const currentOptionsByBranch = computed<string[] | null>(() => {
  const q = currentQuestion.value
  if (!q.optionsByBranch || !q.branchOn) return null
  const branchValue = props.answers[q.branchOn as keyof Answers] as string
  return q.optionsByBranch[branchValue] ?? []
})

function currentValue(): string | string[] {
  return (props.answers as unknown as Record<string, string | string[]>)[currentQuestion.value.id]
}

function setCurrentValue(value: string | string[]) {
  ;(props.answers as unknown as Record<string, string | string[]>)[currentQuestion.value.id] = value
}

function goNext() {
  if (index.value < visibleQuestions.value.length - 1) {
    index.value += 1
  } else {
    emit('complete')
  }
}

function goBack() {
  if (index.value > 0) {
    index.value -= 1
  } else {
    emit('exit')
  }
}

function selectSingle(value: string) {
  setCurrentValue(value)
  goNext()
}

function isMultiSelected(value: string): boolean {
  return (currentValue() as string[]).includes(value)
}

function toggleMulti(value: string, exclusive?: boolean) {
  const q = currentQuestion.value
  const current = [...((currentValue() as string[]) ?? [])]
  const exclusiveValues = (q.options ?? []).filter((o) => o.exclusive).map((o) => o.value)

  let next: string[]
  if (current.includes(value)) {
    next = current.filter((v) => v !== value)
  } else if (exclusive) {
    next = [value]
  } else {
    next = [...current.filter((v) => !exclusiveValues.includes(v)), value]
  }
  setCurrentValue(next)
}

function canProceedMulti(): boolean {
  return ((currentValue() as string[]) ?? []).length > 0
}
</script>

<template>
  <div class="issue_diagnosis_wizard">
    <div class="issue_diagnosis_header">
      <button type="button" class="issue_diagnosis_back" @click="goBack">← ひとつ前に戻る</button>
      <div class="issue_diagnosis_progress">
        <span class="issue_diagnosis_progress_label">
          ブロック{{ blockIndex + 1 }}／{{ BLOCKS.length }}・あと{{ remaining }}問
        </span>
        <div class="issue_diagnosis_progress_bar">
          <div class="issue_diagnosis_progress_bar_fill" :style="{ width: progressPercent + '%' }"></div>
        </div>
      </div>
    </div>

    <div class="issue_diagnosis_block_heading">{{ currentQuestion.block }}</div>

    <div :key="currentQuestion.id" class="issue_diagnosis_question">
      <p class="issue_diagnosis_question_label">{{ currentQuestion.label }}</p>

      <div v-if="currentQuestion.type === 'single'" class="issue_diagnosis_options">
        <button
          v-for="opt in currentQuestion.options"
          :key="opt.value"
          type="button"
          class="issue_diagnosis_option"
          :class="{ issue_diagnosis_option_selected: currentValue() === opt.value }"
          @click="selectSingle(opt.value)"
        >
          {{ opt.text }}
        </button>
        <button
          v-if="currentQuestion.escape"
          type="button"
          class="issue_diagnosis_escape"
          @click="selectSingle(currentQuestion.escape.value)"
        >
          {{ currentQuestion.escape.text }}
        </button>
      </div>

      <div v-else-if="currentOptionsByBranch" class="issue_diagnosis_options">
        <button
          v-for="text in currentOptionsByBranch"
          :key="text"
          type="button"
          class="issue_diagnosis_option issue_diagnosis_option_multi"
          :class="{ issue_diagnosis_option_selected: isMultiSelected(text) }"
          @click="toggleMulti(text)"
        >
          {{ text }}
        </button>
        <button type="button" class="issue_diagnosis_next" :disabled="!canProceedMulti()" @click="goNext">次へ</button>
      </div>

      <div v-else class="issue_diagnosis_options">
        <button
          v-for="opt in currentQuestion.options"
          :key="opt.value"
          type="button"
          class="issue_diagnosis_option issue_diagnosis_option_multi"
          :class="{ issue_diagnosis_option_selected: isMultiSelected(opt.value) }"
          @click="toggleMulti(opt.value, opt.exclusive)"
        >
          {{ opt.text }}
        </button>
        <button type="button" class="issue_diagnosis_next" :disabled="!canProceedMulti()" @click="goNext">次へ</button>
      </div>
    </div>
  </div>
</template>
